import { z } from 'zod';
import { BooleanQuery, Dateish, intList } from './common';
import { htmlToPlainText } from './sgr';

const HHMM = z.string().regex(/^\d{2}:\d{2}$/, 'HH:MM');
const ISODate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD');

// ── Agendas ─────────────────────────────────────────────────────────────────
export const CalendarSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  color: z.string(),
  ownerId: z.number().int().nullable(),
  position: z.number().int(),
});
export type Calendar = z.infer<typeof CalendarSchema>;

export const CalendarBodySchema = z.object({
  name: z.string().min(1),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
});
export type CalendarBody = z.infer<typeof CalendarBodySchema>;

// ── Compromissos ────────────────────────────────────────────────────────────
/**
 * Estado do TICKET do compromisso (spec 039), de `closed_at`/`canceled_at`:
 * fechado vence cancelado, a mesma ordem de `ticketSituation`.
 */
export const COMMITMENT_TICKET_STATES = ['open', 'closed', 'canceled'] as const;
export const CommitmentTicketStateSchema = z.enum(COMMITMENT_TICKET_STATES);
export type CommitmentTicketState = z.infer<typeof CommitmentTicketStateSchema>;

/**
 * Spec 039, adendo 3: o agendamento por PERÍODO — de manhã ou à tarde, sem hora
 * combinada (pedido da PoolTrack, 08/10). `null` é horário exato, o que sempre
 * existiu. É o mesmo par que o cliente escolhe no bot da Blip (`BOT_PERIODS`).
 */
export const COMMITMENT_PERIODS = ['manha', 'tarde'] as const;
export const CommitmentPeriodSchema = z.enum(COMMITMENT_PERIODS);
export type CommitmentPeriod = z.infer<typeof CommitmentPeriodSchema>;

export const COMMITMENT_PERIOD_LABELS: Record<CommitmentPeriod, string> = {
  manha: 'Manhã',
  tarde: 'Tarde',
};

const COMMITMENT_PERIOD_WINDOWS: Record<CommitmentPeriod, { startTime: string; endTime: string }> = {
  manha: { startTime: '08:00', endTime: '12:00' },
  tarde: { startTime: '13:00', endTime: '18:00' },
};

/**
 * A janela do período: manhã 08:00–12:00, tarde 13:00–18:00. Com período, o
 * servidor grava `start_time`/`end_time` com ela — a ordenação, a "Agenda" de
 * sempre e qualquer leitor antigo continuam funcionando; o período é a verdade
 * para a tela. Uma fonte só: o "Agendar" a partir do bot abre com a mesma
 * (`botPeriodWindow`).
 */
export function commitmentPeriodWindow(period: CommitmentPeriod): { startTime: string; endTime: string } {
  return { ...COMMITMENT_PERIOD_WINDOWS[period] };
}

export const CommitmentSchema = z.object({
  id: z.number().int(),
  clientId: z.number().int(),
  clientName: z.string(),
  ticketNumber: z.number().int(),
  ticketTitle: z.string(),
  /**
   * spec 039: o rótulo de recurso do ticket pelo perfil do tenant (spec 017) —
   * o MESMO `resourceLabel` da lista de tickets; null quando não há.
   */
  ticketPlate: z.string().nullable(),
  /** mesa e etapa ATUAIS do ticket, para acompanhar o andamento (spec 039) */
  ticketDeskName: z.string().nullable(),
  ticketStageName: z.string().nullable(),
  ticketState: CommitmentTicketStateSchema,
  /**
   * spec 039 (adendo): o ticket está escondido pela ocultação do tenant
   * (`ticketHidden()` de ticket-mirror.ts, nunca refeito aqui). A tela marca
   * "Oculto no ERP" e não oferece abrir o ticket — o detalhe dele é 404.
   */
  ticketHidden: z.boolean(),
  /**
   * spec 039 (adendo 4): o ticket é espelhado do TiFlux (`ticketMirrored()` de
   * ticket-mirror.ts, nunca refeito aqui). O "feito" dele só marca — o
   * apontamento desse ticket é feito no TiFlux —, e "Concluir e encerrar" some.
   */
  ticketMirrored: z.boolean(),
  assigneeUserId: z.number().int().nullable(),
  assigneeName: z.string().nullable(),
  calendarId: z.number().int().nullable(),
  calendarName: z.string().nullable(),
  calendarColor: z.string().nullable(),
  startDate: ISODate,
  endDate: ISODate,
  startTime: HHMM,
  endTime: HHMM,
  /**
   * adendo 3: `manha | tarde` é agendamento por período, e `startTime`/`endTime`
   * são a janela dele (`commitmentPeriodWindow`); null é horário exato
   */
  period: CommitmentPeriodSchema.nullable(),
  reminderMinutes: z.number().int(),
  description: z.string(),
  done: z.boolean(),
  /**
   * spec 039 (adendo 4): o apontamento que o "Marcar como feito" gerou no
   * ticket (duração zero, "Serviço concluído"). null: não feito, ticket
   * espelhado, feito antes do adendo, ou o apontamento apagado no ticket.
   */
  doneAppointmentId: z.number().int().nullable(),
  createdAt: Dateish,
});
export type Commitment = z.infer<typeof CommitmentSchema>;

export const CommitmentBodySchema = z
  .object({
    clientId: z.number().int().positive(),
    ticketNumber: z.number().int().positive(),
    /** exatamente um: assigneeUserId OU calendarId */
    assigneeUserId: z.number().int().positive().nullish(),
    calendarId: z.number().int().positive().nullish(),
    startDate: ISODate,
    endDate: ISODate,
    startTime: HHMM,
    endTime: HHMM,
    /**
     * adendo 3: o agendamento por período. Com ele, o servidor grava a janela
     * do período e IGNORA `startTime`/`endTime` — o corpo continua o mesmo, e
     * quem não conhece o período segue mandando só os horários. Ausente ou
     * null, horário exato: editar sem ele tira o período.
     */
    period: CommitmentPeriodSchema.nullish(),
    reminderMinutes: z.number().int().min(0).default(0),
    /**
     * spec 039: opcional — o "Agendar" pela placa tem observação opcional. A
     * coluna é `text not null`, e `''` cabe. O "Novo compromisso" continua
     * exigindo a descrição na tela.
     */
    description: z.string().default(''),
  })
  .refine((b) => (b.assigneeUserId != null) !== (b.calendarId != null), {
    message: 'informe atendente OU agenda',
    path: ['assigneeUserId'],
  });
export type CommitmentBody = z.infer<typeof CommitmentBodySchema>;

/**
 * O "quando" curto do compromisso na tela (adendo 3), o que vem antes da placa:
 * com período, "Manhã"/"Tarde" — e não a hora da janela, que ninguém combinou;
 * sem período, a hora de início. "Tarde ABC1D23", "08:30 ABC1D23".
 */
export function commitmentTimeLabel(c: Pick<Commitment, 'period' | 'startTime'>): string {
  return c.period ? COMMITMENT_PERIOD_LABELS[c.period] : c.startTime;
}

/** O "quando" com a faixa: com período, só ele; sem, "HH:MM - HH:MM". */
export function commitmentTimeRangeLabel(c: Pick<Commitment, 'period' | 'startTime' | 'endTime'>): string {
  return c.period ? COMMITMENT_PERIOD_LABELS[c.period] : `${c.startTime} - ${c.endTime}`;
}

// ── Ausências ───────────────────────────────────────────────────────────────
export const AbsenceSchema = z.object({
  id: z.number().int(),
  userId: z.number().int(),
  userName: z.string(),
  startDate: ISODate,
  endDate: ISODate,
  description: z.string().nullable(),
});
export type Absence = z.infer<typeof AbsenceSchema>;

export const AbsenceBodySchema = z.object({
  userId: z.number().int().positive(),
  startDate: ISODate,
  endDate: ISODate,
  description: z.string().nullish(),
});
export type AbsenceBody = z.infer<typeof AbsenceBodySchema>;

// ── Eventos unificados do calendário ────────────────────────────────────────
export const AgendaAppointmentEventSchema = z.object({
  id: z.number().int(),
  ticketNumber: z.number().int(),
  ticketTitle: z.string(),
  clientName: z.string(),
  userId: z.number().int(),
  userName: z.string(),
  /** dia local (America/Sao_Paulo) do início */
  date: ISODate,
  startTime: HHMM,
  endTime: HHMM.nullable(),
  description: z.string().nullable(),
});
export type AgendaAppointmentEvent = z.infer<typeof AgendaAppointmentEventSchema>;

/** Os três tipos do calendário — o `types` de GET /agenda/events (spec 039). */
export const AGENDA_EVENT_TYPES = ['appointments', 'commitments', 'absences'] as const;
export const AgendaEventTypeSchema = z.enum(AGENDA_EVENT_TYPES);
export type AgendaEventType = z.infer<typeof AgendaEventTypeSchema>;

/** "commitments,absences" → tipos; lixo é 422, não filtro vazio. */
const agendaEventTypeList = z
  .string()
  .transform((s) =>
    s
      .split(',')
      .map((x) => x.trim())
      .filter((x) => x !== '')
  )
  .pipe(z.array(AgendaEventTypeSchema).min(1).max(AGENDA_EVENT_TYPES.length));

export const AgendaEventsQuerySchema = z.object({
  start: ISODate,
  end: ISODate,
  user_ids: intList.optional(),
  calendar_ids: intList.optional(),
  /**
   * spec 039: só estes tipos. Sem `types`, vêm os três, como sempre. A página
   * inicial pede só `commitments` de 30 dias, sem centenas de apontamentos.
   */
  types: agendaEventTypeList.optional(),
  /**
   * spec 039 (adendo): também os COMPROMISSOS de tickets ocultos ("Mostrar
   * clientes ocultos" e a página inicial). Sem ele, ficam de fora, como
   * sempre. Apontamentos e ausências não mudam.
   */
  include_hidden: BooleanQuery.optional(),
});
export type AgendaEventsQuery = z.infer<typeof AgendaEventsQuerySchema>;

// ── A busca do "Agendar" (spec 039, adendo) ─────────────────────────────────
/** Quantos tickets a busca do "Agendar" devolve. */
export const AGENDA_TICKET_SEARCH_LIMIT = 20;

/**
 * `GET /agenda/tickets`: a leitura própria da agenda. O casamento é o MESMO da
 * busca da lista de tickets (spec 012); a diferença é `include_hidden`, que a
 * lista geral não tem.
 */
export const AgendaTicketSearchQuerySchema = z.object({
  search: z.string().trim().min(1).max(200),
  include_hidden: BooleanQuery.optional(),
});
export type AgendaTicketSearchQuery = z.infer<typeof AgendaTicketSearchQuerySchema>;

export const AgendaTicketSearchItemSchema = z.object({
  ticketNumber: z.number().int(),
  title: z.string(),
  /** o rótulo de recurso pelo perfil do tenant (spec 017), ou null */
  resourceLabel: z.string().nullable(),
  clientId: z.number().int(),
  clientName: z.string(),
  deskName: z.string(),
  stageName: z.string(),
  state: CommitmentTicketStateSchema,
  /** escondido pela ocultação do tenant — só vem com `include_hidden` */
  hidden: z.boolean(),
});
export type AgendaTicketSearchItem = z.infer<typeof AgendaTicketSearchItemSchema>;

export const AgendaEventsSchema = z.object({
  appointments: z.array(AgendaAppointmentEventSchema),
  commitments: z.array(CommitmentSchema),
  absences: z.array(AbsenceSchema),
});
export type AgendaEvents = z.infer<typeof AgendaEventsSchema>;

// ── "Aguardando data" (spec 039, adendo 2) ──────────────────────────────────
/**
 * A etapa é de AGENDAMENTO? O nome contém "agend", sem acento e sem caixa
 * ("7.1- Agendado", "3-Comunicar Agendamento", "1-Aguardando data agendada").
 * Uma função só, com nome: quando outro tenant precisar, ela vira uma marca na
 * etapa, e quem pergunta não muda. A API aplica esta função aos nomes das
 * etapas e passa ao banco só os ids — a regra não é refeita em SQL.
 */
export function isSchedulingStage(stageName: string): boolean {
  return stageName
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .includes('agend');
}

/**
 * O período que o cliente escolhe no bot da Blip — o mesmo par do agendamento
 * por período (adendo 3): a resposta do bot vira o período do compromisso.
 */
export const BOT_PERIODS = COMMITMENT_PERIODS;
export const BotPeriodSchema = CommitmentPeriodSchema;
export type BotPeriod = CommitmentPeriod;

export const BOT_PERIOD_LABELS: Record<BotPeriod, string> = COMMITMENT_PERIOD_LABELS;

/**
 * A resposta do período no texto do bot (`… Resposta: Tarde`), sem caixa. O
 * texto é formato de TERCEIRO (a Blip grava a pergunta e a resposta como
 * comunicação no TiFlux, que o espelho traz): mudou a forma, muda aqui, e o
 * teste guarda um exemplo real dela.
 */
const BOT_PERIOD_ANSWER = /resposta:\s*(manh[ãa]|tarde)/gi;

/**
 * O período de UMA comunicação, ou null. O HTML sai antes (tags, entidades do
 * editor do TiFlux, `&nbsp;`, quebra entre blocos). Duas respostas na mesma
 * comunicação: a última do texto.
 */
export function botPeriodOfText(body: string): BotPeriod | null {
  let period: BotPeriod | null = null;
  for (const m of htmlToPlainText(body).matchAll(BOT_PERIOD_ANSWER)) {
    period = (m[1] ?? '').toLowerCase() === 'tarde' ? 'tarde' : 'manha';
  }
  return period;
}

const instant = (d: Date | string) => new Date(d).getTime();

/**
 * O período que o cliente escolheu no bot: entre as comunicações do ticket
 * que RESPONDEM o período, a mais recente (pela data; empate, o id maior).
 * Uma comunicação posterior que não responde o período — a confirmação do
 * endereço, um "obrigado" — NÃO apaga a resposta anterior. Sem nenhuma, null.
 */
export function botPeriodOf(
  comments: ReadonlyArray<{ id: number; body: string; createdAt: Date | string }>
): BotPeriod | null {
  const newestFirst = [...comments].sort(
    (a, b) => instant(b.createdAt) - instant(a.createdAt) || b.id - a.id
  );
  for (const c of newestFirst) {
    const period = botPeriodOfText(c.body);
    if (period) return period;
  }
  return null;
}

/** O horário padrão do "Agendar" (spec 039): 08:00–09:00. */
export const AGENDA_DEFAULT_WINDOW = { startTime: '08:00', endTime: '09:00' } as const;

/**
 * A janela com que o "Agendar" abre a partir do período do bot: a do período
 * (`commitmentPeriodWindow`: manhã 08:00–12:00, tarde 13:00–18:00); sem
 * período, o padrão de sempre. O dia quem escolhe é o atendente.
 */
export function botPeriodWindow(period: BotPeriod | null): { startTime: string; endTime: string } {
  return period ? commitmentPeriodWindow(period) : { ...AGENDA_DEFAULT_WINDOW };
}

/** Quantos tickets a lista "Aguardando data" devolve; o total vem ao lado. */
export const AGENDA_AWAITING_SCHEDULE_LIMIT = 300;

/**
 * `GET /agenda/awaiting-schedule`: os tickets ABERTOS em etapa de agendamento
 * (`isSchedulingStage`) sem compromisso em aberto (`done = false`) — feito ou
 * excluído não conta, e o atrasado está no calendário. As permissões são as
 * da busca da agenda (mesas visíveis, `view_others`). `include_hidden` é o
 * switch da aba: sem ele, os ocultos ficam de fora, como em toda tela.
 */
export const AgendaAwaitingScheduleQuerySchema = z.object({
  include_hidden: BooleanQuery.optional(),
});
export type AgendaAwaitingScheduleQuery = z.infer<typeof AgendaAwaitingScheduleQuerySchema>;

export const AgendaAwaitingItemSchema = z.object({
  ticketNumber: z.number().int(),
  title: z.string(),
  /** o rótulo de recurso pelo perfil do tenant (spec 017), ou null */
  resourceLabel: z.string().nullable(),
  clientId: z.number().int(),
  clientName: z.string(),
  deskName: z.string(),
  stageName: z.string(),
  /** desde quando está na etapa — a lista vem dos mais antigos */
  stageEnteredAt: Dateish,
  /** o período que o cliente escolheu no bot (`botPeriodOf`), ou null */
  botPeriod: BotPeriodSchema.nullable(),
  /** escondido pela ocultação do tenant — só vem com `include_hidden` */
  hidden: z.boolean(),
});
export type AgendaAwaitingItem = z.infer<typeof AgendaAwaitingItemSchema>;

export const AgendaAwaitingScheduleSchema = z.object({
  /** até AGENDA_AWAITING_SCHEDULE_LIMIT, há mais tempo na etapa primeiro */
  items: z.array(AgendaAwaitingItemSchema),
  /** todos os que entram, mesmo além do teto */
  total: z.number().int(),
});
export type AgendaAwaitingSchedule = z.infer<typeof AgendaAwaitingScheduleSchema>;

// ── Situação do compromisso (spec 039) ─────────────────────────────────────
/**
 * "Em que pé está" um agendamento, até a finalização — na ordem da
 * precedência, que `commitmentSituation` escreve de cima para baixo.
 */
export const COMMITMENT_SITUATIONS = [
  'done',
  'ticket_closed',
  'ticket_canceled',
  'overdue',
  'today',
  'scheduled',
] as const;
export type CommitmentSituation = (typeof COMMITMENT_SITUATIONS)[number];

export const COMMITMENT_SITUATION_LABELS: Record<CommitmentSituation, string> = {
  done: 'Feito',
  ticket_closed: 'Ticket finalizado',
  ticket_canceled: 'Ticket cancelado',
  overdue: 'Atrasado',
  today: 'Hoje',
  scheduled: 'Agendado',
};

/** O fuso do produto (org.ts: "o fuso NÃO viaja"). */
const AGENDA_TIME_ZONE = 'America/Sao_Paulo';
const spDayFormat = new Intl.DateTimeFormat('en-CA', {
  timeZone: AGENDA_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * O dia (YYYY-MM-DD) de um instante em America/Sao_Paulo — nunca o UTC do
 * servidor nem o fuso do navegador: 23:30 em São Paulo já é o dia seguinte em
 * UTC.
 */
export function saoPauloDay(now: Date): string {
  return spDayFormat.format(now);
}

/** YYYY-MM-DD ± n dias, em aritmética de calendário (sem fuso, sem horário de verão). */
export function addDaysISO(day: string, n: number): string {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, (d ?? 1) + n)).toISOString().slice(0, 10);
}

/** A semana corrente, de segunda a domingo, no dia de São Paulo. */
export function saoPauloWeek(now: Date): { start: string; end: string } {
  const today = saoPauloDay(now);
  const [y, m, d] = today.split('-').map(Number);
  const weekday = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1)).getUTCDay(); // 0 = domingo
  const start = addDaysISO(today, -((weekday + 6) % 7));
  return { start, end: addDaysISO(start, 6) };
}

/**
 * A situação do compromisso, com a precedência ESCRITA (spec 039):
 *
 *   feito > ticket finalizado > ticket cancelado > atrasado > hoje > agendado
 *
 * - feito: a pessoa marcou;
 * - ticket finalizado/cancelado: o ticket acabou sem o compromisso marcado —
 *   conta como concluído, e a tela diz que foi pelo ticket;
 * - atrasado: o último dia já passou;
 * - hoje: o dia de hoje está no período; agendado: o resto.
 *
 * "Hoje" é `saoPauloDay(now)`: quem chama passa o INSTANTE, e não um dia que
 * possa ter saído do UTC. O horário do compromisso não entra — atrasado é dia
 * que passou, não hora.
 */
export function commitmentSituation(
  c: Pick<Commitment, 'done' | 'ticketState' | 'startDate' | 'endDate'>,
  now: Date
): CommitmentSituation {
  if (c.done) return 'done';
  if (c.ticketState === 'closed') return 'ticket_closed';
  if (c.ticketState === 'canceled') return 'ticket_canceled';
  const today = saoPauloDay(now);
  if (c.endDate < today) return 'overdue';
  if (c.startDate <= today) return 'today';
  return 'scheduled';
}

// ── "Minha agenda da semana" (spec 039, página inicial) ─────────────────────
/** Quantos dias para trás a página inicial procura atrasados. */
export const MY_AGENDA_OVERDUE_DAYS = 30;

/**
 * O período que a página inicial pede à API: dos 30 dias anteriores a hoje até
 * o domingo da semana corrente — um pedido só, cobrindo atrasados e semana.
 */
export function myWeekAgendaRange(now: Date): { start: string; end: string } {
  return {
    start: addDaysISO(saoPauloDay(now), -MY_AGENDA_OVERDUE_DAYS),
    end: saoPauloWeek(now).end,
  };
}

export interface MyWeekAgenda {
  week: { start: string; end: string };
  /** os meus atrasados dos 30 dias anteriores, mais antigos primeiro */
  overdue: Commitment[];
  /** a semana (segunda a domingo), só os dias com compromisso, sem os atrasados */
  days: Array<{ date: string; items: Commitment[] }>;
}

const byStart = (a: Commitment, b: Commitment) =>
  a.startDate.localeCompare(b.startDate) || a.startTime.localeCompare(b.startTime) || a.id - b.id;

/**
 * Os compromissos ATRIBUÍDOS a `userId` (spec 039): no topo, os atrasados dos
 * 30 dias anteriores; depois a semana corrente agrupada por dia. Um atrasado
 * desta semana (segunda, vista na quinta) fica SÓ em "Atrasados" — a mesma
 * linha não aparece duas vezes. Compromisso de vários dias que começou antes
 * da semana entra no primeiro dia dela.
 */
export function myWeekAgenda(commitments: Commitment[], userId: number, now: Date): MyWeekAgenda {
  const week = saoPauloWeek(now);
  const from = addDaysISO(saoPauloDay(now), -MY_AGENDA_OVERDUE_DAYS);
  const mine = commitments.filter((c) => c.assigneeUserId === userId).sort(byStart);

  const overdue = mine.filter((c) => commitmentSituation(c, now) === 'overdue' && c.endDate >= from);
  const late = new Set(overdue.map((c) => c.id));

  const byDay = new Map<string, Commitment[]>();
  for (const c of mine) {
    if (late.has(c.id) || c.startDate > week.end || c.endDate < week.start) continue;
    const day = c.startDate < week.start ? week.start : c.startDate;
    const list = byDay.get(day);
    if (list) list.push(c);
    else byDay.set(day, [c]);
  }
  const days = [...byDay.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, items]) => ({ date, items }));
  return { week, overdue, days };
}
